// Generation history repository — the storage seam.
//
// `createHistoryRepository()` returns the implementation the studio uses today:
// IndexedDB, on this device. The MagicHistoryRepository interface is the
// contract the hook and UI depend on, so once the backend grows a history API
// we add a `BackendHistoryRepository` here (GET list / POST add / DELETE
// remove+clear) and switch the factory — nothing in the components changes.

import { dbAdd, dbClear, dbGet, dbList, dbRemove } from './db';
import { MagicGeneration, MagicHistoryRepository } from './types';

// Keep history bounded so it can't grow without limit on disk. The newest
// MAX_ENTRIES per user are kept; older ones are evicted on insert. Generated
// images are a few MB each, so this stays comfortably within IndexedDB quota.
const MAX_ENTRIES = 60;

class IndexedDbHistoryRepository implements MagicHistoryRepository {
  list(userId: string): Promise<MagicGeneration[]> {
    return dbList(userId);
  }

  async add(record: MagicGeneration): Promise<void> {
    await dbAdd(record);
    // Evict anything beyond the cap (newest kept). Best-effort — failure here
    // must not break the just-completed save.
    try {
      const all = await dbList(record.userId);
      const stale = all.slice(MAX_ENTRIES);
      await Promise.all(stale.map((r) => dbRemove(r.id)));
    } catch {
      // ignore eviction errors
    }
  }

  async update(id: string, patch: Partial<MagicGeneration>): Promise<void> {
    const existing = await dbGet(id);
    if (!existing) return; // nothing to patch (never saved / evicted)
    await dbAdd({ ...existing, ...patch });
  }

  remove(id: string): Promise<void> {
    return dbRemove(id);
  }

  clear(userId: string): Promise<void> {
    return dbClear(userId);
  }
}

let repository: MagicHistoryRepository | null = null;

export function createHistoryRepository(): MagicHistoryRepository {
  if (!repository) {
    repository = new IndexedDbHistoryRepository();
  }
  return repository;
}
